import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useMatch } from "../lib/MatchContext";
import CountdownClock from "./CountdownClock";
import {
  LayoutDashboard, ClipboardCheck, Radio, TriangleAlert,
  Trophy, LogOut, ShieldCheck, AlertOctagon, CalendarClock, Download, Users, Boxes, MapPinned, LifeBuoy, Users2, Map, Menu, X,
} from "lucide-react";

const sections = [
  {
    label: null,
    items: [
      { to: "/matches", label: "Matches", icon: Trophy, testid: "nav-matches" },
      { to: "/", label: "Command Centre", icon: LayoutDashboard, testid: "nav-dashboard", scoped: true },
    ],
  },
  {
    label: "Pre-Match",
    items: [
      { to: "/pre-match/raise-issue", label: "Raise Issue", icon: AlertOctagon, testid: "nav-raise-issue", scoped: true },
      { to: "/pre-match/tracker", label: "Pre-Match Tracker", icon: CalendarClock, testid: "nav-pre-match-tracker", scoped: true },
      { to: "/pre-match/venue", label: "Issues Post Inspection", icon: ClipboardCheck, testid: "nav-venue", scoped: true },
      { to: "/pre-match/stocks", label: "Stocks", icon: Boxes, testid: "nav-stocks", scoped: true },
      { to: "/pre-match/allocations", label: "Allocations", icon: MapPinned, testid: "nav-allocations", scoped: true },
    ],
  },
  {
    label: "Match Day",
    items: [
      { to: "/match-day/runsheet", label: "Run Sheet", icon: Radio, testid: "nav-runsheet", scoped: true },
      { to: "/match-day/incidents", label: "Critical Incident Log", icon: TriangleAlert, testid: "nav-incidents", scoped: true },
      { to: "/readiness", label: "Readiness Certs", icon: ShieldCheck, testid: "nav-readiness", scoped: true },
    ],
  },
  {
    label: "Critical Info",
    items: [
      { to: "/critical-info/maps", label: "Maps", icon: Map, testid: "nav-maps", scoped: true },
      { to: "/critical-info/responsible-persons", label: "Responsible Persons", icon: Users2, testid: "nav-responsible-persons", scoped: true },
      { to: "/critical-info/emergency-plans", label: "Important Documents", icon: LifeBuoy, testid: "nav-emergency-plans", scoped: true },
    ],
  },
  {
    label: "Post-Match",
    items: [
      { to: "/post-match/export", label: "Export & Report", icon: Download, testid: "nav-export", scoped: true },
    ],
  },
  {
    label: "Admin",
    permission: "admin.users",
    permissionAny: ["admin.users", "vendors.manage"],
    items: [
      { to: "/admin/users", label: "User Management", icon: Users, testid: "nav-admin-users" },
    ],
  },
];

// Flatten for mobile bottom nav — every section item is exposed via a horizontally-scrollable bar
const mobileTabs = [
  { to: "/matches", label: "Matches", icon: Trophy, testid: "nav-matches" },
  { to: "/", label: "Command", icon: LayoutDashboard, testid: "nav-dashboard", scoped: true },
  { to: "/pre-match/raise-issue", label: "Issue", icon: AlertOctagon, testid: "nav-raise-issue", scoped: true },
  { to: "/pre-match/tracker", label: "Pre-Match", icon: CalendarClock, testid: "nav-pre-match-tracker", scoped: true },
  { to: "/pre-match/venue", label: "Inspection", icon: ClipboardCheck, testid: "nav-venue", scoped: true },
  { to: "/pre-match/stocks", label: "Stocks", icon: Boxes, testid: "nav-stocks", scoped: true },
  { to: "/pre-match/allocations", label: "Allocations", icon: MapPinned, testid: "nav-allocations", scoped: true },
  { to: "/match-day/runsheet", label: "Run", icon: Radio, testid: "nav-runsheet", scoped: true },
  { to: "/match-day/incidents", label: "Critical Incident", icon: TriangleAlert, testid: "nav-incidents", scoped: true },
  { to: "/readiness", label: "Readiness", icon: ShieldCheck, testid: "nav-readiness", scoped: true },
  { to: "/critical-info", label: "Critical", icon: LifeBuoy, testid: "nav-critical-info", scoped: true },
  { to: "/post-match/export", label: "Export", icon: Download, testid: "nav-export", scoped: true },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users", permission: "admin.users" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { selectedMatchId, selectedMatch } = useMatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Auto-close mobile drawer on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const isVendor = user?.role === "vendor";
  const visibleSections = isVendor
    ? [
        {
          label: null,
          items: [
            { to: "/matches", label: "Matches", icon: Trophy, testid: "nav-matches" },
          ],
        },
        {
          label: "Vendor Console",
          items: [
            { to: "/pre-match/tracker", label: "Pre-Match Tracker", icon: CalendarClock, testid: "nav-pre-match-tracker", scoped: true },
            { to: "/pre-match/raise-issue", label: "Raise Issue", icon: AlertOctagon, testid: "nav-raise-issue", scoped: true },
          ],
        },
      ]
    : sections;
  const visibleMobileTabs = isVendor
    ? [
        { to: "/matches", label: "Matches", icon: Trophy, testid: "nav-matches" },
        { to: "/pre-match/tracker", label: "Pre-Match", icon: CalendarClock, testid: "nav-pre-match-tracker", scoped: true },
        { to: "/pre-match/raise-issue", label: "Issue", icon: AlertOctagon, testid: "nav-raise-issue", scoped: true },
      ]
    : mobileTabs;

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-obsidian text-white">
      {/* Sidebar */}
      <aside className="w-60 border-r border-cmdBorder bg-[#0A0A0A] hidden md:flex flex-col" data-testid="sidebar">
        <div className="p-5 border-b border-cmdBorder">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border border-white flex items-center justify-center">
              <span className="font-head font-black text-lg leading-none">M</span>
            </div>
            <div>
              <div className="font-head text-lg font-black tracking-tight leading-none">MATCH<span className="text-readyGreen">COMMAND</span></div>
              <div className="mc-label mt-1">Stadium Operations</div>
            </div>
          </div>
        </div>
        <div className="border-b border-cmdBorder p-4">
          <div className="mc-label mb-2">Signed In</div>
          <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate" data-testid="user-role">
            {user?.role?.replace(/_/g, " ")} · {user?.department}
          </div>
          <div className="mt-3 border border-cmdBorder p-2 bg-black/40" data-testid="sidebar-active-match">
            <div className="mc-label">Active Match</div>
            {selectedMatch ? (
              <div className="text-xs text-readyGreen font-mono-live truncate mt-0.5" title={selectedMatch.name}>
                {selectedMatch.name}
              </div>
            ) : (
              <div className="text-xs text-warnAmber font-mono-live mt-0.5">— none selected —</div>
            )}
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest border border-cmdBorder hover:border-white py-2 transition-colors duration-150"
          >
            <LogOut className="w-3 h-3" /> Log out
          </button>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto">
          {visibleSections
            .filter((s) => {
              const perms = user?.permissions || [];
              if (s.permissionAny) return s.permissionAny.some((p) => perms.includes(p));
              return !s.permission || perms.includes(s.permission);
            })
            .map((section, si) => (
            <div key={si} className="pb-2">
              {section.label && (
                <div className="mc-label px-5 pt-4 pb-1.5 flex items-center gap-2">
                  <span>{section.label}</span>
                  <span className="flex-1 h-px bg-cmdBorder" />
                </div>
              )}
              {section.items.map(({ to, label, icon: Icon, testid, scoped }) => {
                const gated = scoped && !selectedMatchId;
                return (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  data-testid={testid}
                  title={gated ? "Select a match first" : undefined}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors duration-150 ` +
                    (isActive
                      ? "border-white bg-white/5 text-white"
                      : gated
                        ? "border-transparent text-zinc-600 hover:text-zinc-400 hover:bg-white/5"
                        : "border-transparent text-zinc-400 hover:text-white hover:bg-white/5")
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="tracking-wide">{label}</span>
                  {gated && <span className="ml-auto text-[9px] text-zinc-600 uppercase tracking-widest">Locked</span>}
                </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* Mobile top-bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-obsidian border-b border-cmdBorder p-3 flex items-center justify-between">
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="border border-cmdBorder p-2 hover:border-white transition-colors"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu className="w-4 h-4" />
        </button>
        <div className="font-head font-black tracking-tight text-lg">MATCH<span className="text-readyGreen">COMMAND</span></div>
        <button onClick={onLogout} className="text-xs uppercase tracking-widest border border-cmdBorder px-2 py-1" data-testid="mobile-logout">Exit</button>
      </div>

      {/* Mobile drawer — full sidebar accessible via hamburger */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-drawer-backdrop" />
          <aside className="relative w-72 max-w-[85vw] bg-[#0A0A0A] border-r border-cmdBorder flex flex-col overflow-y-auto">
            <div className="p-5 border-b border-cmdBorder flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 border border-white flex items-center justify-center">
                  <span className="font-head font-black text-lg leading-none">M</span>
                </div>
                <div>
                  <div className="font-head text-lg font-black tracking-tight leading-none">MATCH<span className="text-readyGreen">COMMAND</span></div>
                  <div className="mc-label mt-1">Stadium Operations</div>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1.5 border border-cmdBorder hover:border-white" data-testid="mobile-drawer-close" aria-label="Close menu">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="border-b border-cmdBorder p-4">
              <div className="mc-label mb-2">Signed In</div>
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">
                {user?.role?.replace(/_/g, " ")}{user?.department ? ` · ${user.department}` : ""}
              </div>
              <div className="mt-3 border border-cmdBorder p-2 bg-black/40">
                <div className="mc-label">Active Match</div>
                {selectedMatch ? (
                  <div className="text-xs text-readyGreen font-mono-live truncate mt-0.5">{selectedMatch.name}</div>
                ) : (
                  <div className="text-xs text-warnAmber font-mono-live mt-0.5">— none selected —</div>
                )}
              </div>
            </div>
            <nav className="flex-1 py-2">
              {visibleSections
                .filter((s) => {
                  const perms = user?.permissions || [];
                  if (s.permissionAny) return s.permissionAny.some((p) => perms.includes(p));
                  return !s.permission || perms.includes(s.permission);
                })
                .map((section, si) => (
                <div key={si} className="pb-2">
                  {section.label && (
                    <div className="mc-label px-5 pt-4 pb-1.5 flex items-center gap-2">
                      <span>{section.label}</span>
                      <span className="flex-1 h-px bg-cmdBorder" />
                    </div>
                  )}
                  {section.items.map(({ to, label, icon: Icon, testid, scoped }) => {
                    const gated = scoped && !selectedMatchId;
                    return (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === "/"}
                      data-testid={`drawer-${testid}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-5 py-3 text-sm border-l-2 transition-colors ` +
                        (isActive
                          ? "border-white bg-white/5 text-white"
                          : gated
                            ? "border-transparent text-zinc-600"
                            : "border-transparent text-zinc-300 hover:text-white hover:bg-white/5")
                      }
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      <span className="tracking-wide">{label}</span>
                      {gated && <span className="ml-auto text-[9px] text-zinc-600 uppercase tracking-widest">Locked</span>}
                    </NavLink>
                    );
                  })}
                </div>
              ))}
            </nav>
            <button
              onClick={onLogout}
              className="m-4 flex items-center justify-center gap-2 text-xs uppercase tracking-widest border border-cmdBorder hover:border-white py-2.5 transition-colors"
              data-testid="mobile-drawer-logout"
            >
              <LogOut className="w-3 h-3" /> Log out
            </button>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-6 md:pb-0">
        {selectedMatch && (
          <div
            className="sticky top-0 md:top-0 z-30 border-b border-cmdBorder bg-obsidian/95 backdrop-blur px-4 md:px-6 py-2 flex items-center justify-between gap-3 flex-wrap"
            data-testid="global-countdown-bar"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="mc-label">Live Match</span>
              <span className="font-head font-bold uppercase tracking-tight truncate">{selectedMatch.name}</span>
            </div>
            <CountdownClock target={selectedMatch.match_date} size="text-lg md:text-xl" />
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
